import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { FiTarget, FiEdit3, FiClock, FiTrendingUp, FiInfo, FiRefreshCw, FiArrowRight, FiBarChart } from 'react-icons/fi'
import { 
  Container, 
  Card, 
  Grid, 
  Heading1, 
  Heading2, 
  Heading3,
  BodyText, 
  SmallText, 
  Button, 
  StatusBadge,
  Input 
} from '../components/ui'
import { theme, mediaQuery } from '../theme'

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing.xl};
  
  ${mediaQuery.mobile} {
    flex-direction: column;
    gap: ${theme.spacing.md};
  }
`

const MetricIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 28px;
  margin-bottom: ${theme.spacing.md};
`

const FormSection = styled(Card)`
  background: linear-gradient(135deg, ${theme.colors.primary.light}20, ${theme.colors.primary.main}10);
  border-left: 4px solid ${theme.colors.primary.main};
`

const InputGroup = styled.div`
  margin-bottom: ${theme.spacing.lg};
`

const Label = styled.label`
  display: block;
  font-weight: 500;
  color: ${theme.colors.primary.dark};
  margin-bottom: ${theme.spacing.sm};
  font-size: 16px;
`



const OptimalPaceDisplay = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing.xl};
`

const MainPace = styled.div`
  font-size: 36px;
  font-weight: 700;
  font-family: ${theme.typography.heading.fontFamily};
  color: ${theme.colors.primary.main};
  line-height: 1.2;
  margin: ${theme.spacing.md} 0;
  
  ${mediaQuery.tablet} {
    font-size: 48px;
  }
  
  ${mediaQuery.desktop} {
    font-size: 56px;
  }
`

const PaceRange = styled.div`
  font-size: 18px;
  color: ${theme.colors.primary.light};
  margin-bottom: ${theme.spacing.md};
  
  ${mediaQuery.tablet} {
    font-size: 20px;
  }
  
  ${mediaQuery.desktop} {
    font-size: 24px;
  }
`

const InfoCard = styled(Card)`
  background: linear-gradient(135deg, ${theme.colors.status.info}20, ${theme.colors.status.info}10);
  border-left: 4px solid ${theme.colors.status.info};
`

const VisualizationCard = styled(Card)`
  background: linear-gradient(135deg, ${theme.colors.secondary.green}20, ${theme.colors.secondary.green}10);
  border-left: 4px solid ${theme.colors.secondary.green};
  margin-bottom: ${theme.spacing.lg};
  width: 100%;
`

const ArousalBar = styled.div`
  width: 100%;
  height: 30px;
  background: ${theme.colors.secondary.beige};
  border-radius: ${theme.borderRadius.md};
  position: relative;
  margin: 40px 0 ${theme.spacing.lg} 0;
  overflow: visible;
`

const ArousalDot = styled.div`
  position: absolute;
  top: 50%;
  left: ${props => (props.level / 10) * 100}%;
  transform: translate(-50%, -50%);
  width: 20px;
  height: 20px;
  background: ${props => props.color || theme.colors.primary.main};
  border: 3px solid white;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  transition: left 0.3s ease;
  z-index: 2;
`

const ArousalLabel = styled.div`
  position: absolute;
  top: -35px;
  left: ${props => (props.level / 10) * 100}%;
  transform: translateX(-50%);
  background: ${props => props.color || theme.colors.primary.main};
  color: white;
  padding: 4px 8px;
  border-radius: ${theme.borderRadius.sm};
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  z-index: 3;
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: ${props => props.color || theme.colors.primary.main};
  }
`

const OptimalZone = styled.div`
  position: absolute;
  top: 0;
  left: ${props => (props.min / 10) * 100}%;
  width: ${props => ((props.max - props.min) / 10) * 100}%;
  height: 100%;
  background: rgba(67, 95, 96, 0.3);
  border: 2px solid ${theme.colors.secondary.green};
  border-radius: ${theme.borderRadius.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: ${theme.colors.secondary.green};
  font-weight: 600;
`

const HoursComparisonGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: ${theme.spacing.md};
  align-items: center;
  margin: ${theme.spacing.lg} 0;
  padding: ${theme.spacing.md};
  background: ${theme.colors.secondary.beige};
  border-radius: ${theme.borderRadius.md};
`

const HoursBox = styled.div`
  text-align: center;
  padding: ${theme.spacing.md};
  background: white;
  border-radius: ${theme.borderRadius.md};
  border: 2px solid ${props => props.highlight ? theme.colors.secondary.green : theme.colors.primary.light};
`

const ArrowIcon = styled.div`
  font-size: 24px;
  color: ${theme.colors.secondary.green};
  display: flex;
  align-items: center;
  justify-content: center;
`

const HBRReference = styled.div`
  margin-top: ${theme.spacing.lg};
  padding: ${theme.spacing.md};
  background: ${theme.colors.primary.light}20;
  border-radius: ${theme.borderRadius.md};
  border-left: 4px solid ${theme.colors.primary.main};
  
  a {
    color: ${theme.colors.primary.main};
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`

const StatusBanner = styled.div`
  width: 100%;
  padding: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  text-align: center;
  background: ${props => {
    switch(props.status) {
      case 'over': return `linear-gradient(135deg, ${theme.colors.status.error}20, ${theme.colors.status.error}10)`
      case 'optimal': return `linear-gradient(135deg, ${theme.colors.secondary.green}20, ${theme.colors.secondary.green}10)`
      case 'under': return `linear-gradient(135deg, ${theme.colors.status.info}20, ${theme.colors.status.info}10)`
      default: return theme.colors.secondary.beige
    }
  }};
  border-left: 6px solid ${props => {
    switch(props.status) {
      case 'over': return theme.colors.status.error
      case 'optimal': return theme.colors.secondary.green
      case 'under': return theme.colors.status.info
      default: return theme.colors.primary.main
    }
  }};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`

const StatusHeadline = styled.h2`
  font-size: 32px;
  font-weight: 700;
  font-family: ${theme.typography.heading.fontFamily};
  margin: 0 0 ${theme.spacing.sm} 0;
  color: ${props => {
    switch(props.status) {
      case 'over': return theme.colors.status.error
      case 'optimal': return theme.colors.secondary.green
      case 'under': return theme.colors.status.info
      default: return theme.colors.primary.main
    }
  }};
  
  ${mediaQuery.tablet} {
    font-size: 40px;
  }
  
  ${mediaQuery.desktop} {
    font-size: 48px;
  }
`

const StatusSubheadline = styled.p`
  font-size: 18px;
  color: ${theme.colors.primary.dark};
  margin: 0;
  font-weight: 500;
  
  ${mediaQuery.tablet} {
    font-size: 20px;
  }
  
  ${mediaQuery.desktop} {
    font-size: 22px;
  }
`

const ArousalScale = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${theme.spacing.sm};
  font-size: 12px;
  color: ${theme.colors.primary.light};
`

const EmotionalFactorsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
`

const FactorInput = styled.div`
  display: flex;
  flex-direction: column;
`

const OptimalPacePage = () => {
  const [hoursWorked, setHoursWorked] = useState('')
  const [demandPoints, setDemandPoints] = useState('')
  const [emotionalFactors, setEmotionalFactors] = useState({
    pressure: '',
    hours: '',
    focus: '',
    calm: '',
    happiness: '',
    depression: '',
    mania: '',
    energy: ''
  })
  const [optimalPace, setOptimalPace] = useState(null)
  const [isCalculating, setIsCalculating] = useState(false)

  // Helper function to convert decimal hours to hours and minutes format
  const formatHoursAndMinutes = (decimalHours) => {
    const hours = Math.floor(decimalHours)
    const minutes = Math.round((decimalHours - hours) * 60)
    
    if (hours === 0) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`
    } else if (minutes === 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''}`
    } else {
      return `${hours} hour${hours !== 1 ? 's' : ''} and ${minutes} minute${minutes !== 1 ? 's' : ''}`
    }
  }

  // Yerkes-Dodson Law Implementation
  const calculateOptimalPace = () => {
    if (!hoursWorked || !emotionalFactors.pressure) {
      return
    }

    setIsCalculating(true)

    // Calculate average emotional arousal (excluding pressure)
    // Invert values since journal scale is inverse to arousal (10 - value)
    const emotionalValues = [
      emotionalFactors.hours,
      emotionalFactors.focus,
      emotionalFactors.calm,
      emotionalFactors.happiness,
      emotionalFactors.depression,
      emotionalFactors.mania,
      emotionalFactors.energy
    ].filter(val => val !== '' && val !== null && val !== undefined)
      .map(val => 10 - parseFloat(val)) // Invert scale for arousal calculation
      .filter(val => !isNaN(val))

    if (emotionalValues.length === 0) {
      setIsCalculating(false)
      return
    }

    const averageArousal = emotionalValues.reduce((sum, val) => sum + val, 0) / emotionalValues.length
    const pressure = parseFloat(emotionalFactors.pressure)
    const hours = parseFloat(hoursWorked)
    const points = demandPoints ? parseFloat(demandPoints) : null

    // Determine task complexity based on pressure
    const isHighComplexity = pressure >= 6

    // Yerkes-Dodson: High complexity tasks need lower optimal arousal and narrower range
    // Low complexity tasks can handle higher arousal with wider tolerance
    let optimalArousalRange
    
    if (isHighComplexity) {
      // For complex tasks (high pressure), optimal arousal is 1-3 with optimal at 2
      optimalArousalRange = { min: 1.0, optimal: 2.0, max: 3.0 }
    } else {
      // For simple tasks (low pressure), optimal arousal is 1-5 with optimal at 3
      optimalArousalRange = { min: 1.0, optimal: 3.0, max: 5.0 }
    }

    // Calculate how far current arousal is from optimal
    const arousalDifference = averageArousal - optimalArousalRange.optimal

    // Calculate scaling ratio based on arousal relationship
    // If current arousal is higher than optimal, we need to reduce workload
    // If current arousal is lower than optimal, we can increase workload
    const arousalRatio = optimalArousalRange.optimal / Math.max(averageArousal, 0.1) // Avoid division by zero

    // Apply arousal ratio with caps: max 100% increase (2x) and max 18 hours for optimal
    const cappedArousalRatio = Math.min(2.0, arousalRatio) // Cap at 100% increase
    const optimalHours = Math.min(18, hours * cappedArousalRatio) // Cap optimal at 18 hours
    
    // Calculate range based on optimal arousal range boundaries with same percentage cap
    const minArousalRatio = Math.min(2.0, optimalArousalRange.min / Math.max(averageArousal, 0.1))
    const maxArousalRatio = Math.min(2.0, optimalArousalRange.max / Math.max(averageArousal, 0.1))
    
    const minHours = hours * minArousalRatio
    const maxHours = Math.min(18, hours * maxArousalRatio) // Also cap max hours at 18

    // Calculate optimal demand points if provided - using identical scaling ratios
    let optimalDemandPoints = null
    let minDemandPoints = null
    let maxDemandPoints = null
    
    if (points) {
      // Apply the same capped scaling ratios to demand points
      optimalDemandPoints = Math.round(points * cappedArousalRatio)
      minDemandPoints = Math.round(points * minArousalRatio)
      maxDemandPoints = Math.round(points * maxArousalRatio)
    }

    setOptimalPace({
      hours: {
        optimal: Math.round(optimalHours * 10) / 10,
        min: Math.round(minHours * 10) / 10,
        max: Math.round(maxHours * 10) / 10
      },
      demandPoints: points ? {
        optimal: optimalDemandPoints,
        min: minDemandPoints,
        max: maxDemandPoints
      } : null,
      currentArousal: Math.round(averageArousal * 10) / 10,
      optimalArousal: optimalArousalRange.optimal,
      isHighComplexity,
      pressure
    })

    setIsCalculating(false)
  }

  const handleFactorChange = (factor, value) => {
    setEmotionalFactors(prev => ({
      ...prev,
      [factor]: value
    }))
  }

  const resetForm = () => {
    setHoursWorked('')
    setDemandPoints('')
    setEmotionalFactors({
      pressure: '',
      hours: '',
      focus: '',
      calm: '',
      happiness: '',
      depression: '',
      mania: '',
      energy: ''
    })
    setOptimalPace(null)
  }

  const getPacingStatus = () => {
    if (!optimalPace) return null
    
    const currentArousal = optimalPace.currentArousal
    const optimalZoneMin = optimalPace.isHighComplexity ? 1 : 1
    const optimalZoneMax = optimalPace.isHighComplexity ? 3 : 5
    
    if (currentArousal > optimalZoneMax) {
      return {
        status: 'over',
        headline: 'Over Pacing',
        subheadline: 'You are eroding your Mental Resilience. Your Optimal Pace is slowing.'
      }
    } else if (currentArousal >= optimalZoneMin && currentArousal <= optimalZoneMax) {
      return {
        status: 'optimal',
        headline: 'At Optimal Pace',
        subheadline: 'You are perfectly supporting your Mental Resilience so your Optimal Pace can hold steady.'
      }
    } else {
      return {
        status: 'under',
        headline: 'Under Pacing',
        subheadline: 'You are restoring your Mental Resilience. Your Optimal Pace is increasing.'
      }
    }
  }

  const getOptimalPaceStatus = () => {
    if (!optimalPace || !optimalPace.hours) return 'info'
    
    const currentHours = parseFloat(hoursWorked)
    if (currentHours >= optimalPace.hours.min && currentHours <= optimalPace.hours.max) {
      return 'excellent'
    } else if (Math.abs(currentHours - optimalPace.hours.optimal) <= 1) {
      return 'good'
    } else {
      return 'fair'
    }
  }

  useEffect(() => {
    // Auto-calculate when we have minimum required data
    if (hoursWorked && emotionalFactors.pressure) {
      calculateOptimalPace()
    }
  }, [hoursWorked, demandPoints, emotionalFactors])

  return (
    <Container>
      <PageHeader>
        <div>
          <MetricIcon>
            <FiTarget />
          </MetricIcon>
          <Heading1>Optimal Pace</Heading1>
          <BodyText>
            Calculate your optimal work pace using the Yerkes-Dodson Law of arousal and performance
          </BodyText>
        </div>
        
        <Button onClick={resetForm} variant="outline">
          <FiRefreshCw style={{ marginRight: '8px' }} />
          Reset
        </Button>
      </PageHeader>

      {/* Status Banner */}
      {optimalPace && getPacingStatus() && (
        <StatusBanner status={getPacingStatus().status}>
          <StatusHeadline status={getPacingStatus().status}>
            {getPacingStatus().headline}
          </StatusHeadline>
          <StatusSubheadline>
            {getPacingStatus().subheadline}
          </StatusSubheadline>
        </StatusBanner>
      )}

      {/* Full width visualization when optimal pace is calculated */}
      {optimalPace && (
        <VisualizationCard>
          <Heading3>
            <FiBarChart style={{ marginRight: '8px' }} />
            Hours and Demand Points Scaling Visualization
          </Heading3>
          
          <BodyText>Current Arousal vs Optimal Zone</BodyText>
          <ArousalBar>
            <OptimalZone 
              min={optimalPace.isHighComplexity ? 1 : 1} 
              max={optimalPace.isHighComplexity ? 3 : 5}
            >
              Optimal Zone
            </OptimalZone>
            <ArousalDot 
              level={optimalPace.currentArousal} 
              color={optimalPace.currentArousal >= (optimalPace.isHighComplexity ? 1 : 1) && 
                     optimalPace.currentArousal <= (optimalPace.isHighComplexity ? 3 : 5) 
                     ? theme.colors.secondary.green : theme.colors.status.warning}
            />
            <ArousalLabel
              level={optimalPace.currentArousal}
              color={optimalPace.currentArousal >= (optimalPace.isHighComplexity ? 1 : 1) && 
                     optimalPace.currentArousal <= (optimalPace.isHighComplexity ? 3 : 5) 
                     ? theme.colors.secondary.green : theme.colors.status.warning}
            >
              Current: {optimalPace.currentArousal}
            </ArousalLabel>
                     </ArousalBar>
           
           <ArousalScale>
             <span>0</span>
             <span>1</span>
             <span>2</span>
             <span>3</span>
             <span>4</span>
             <span>5</span>
             <span>6</span>
             <span>7</span>
             <span>8</span>
             <span>9</span>
             <span>10</span>
           </ArousalScale>
           
                      <HoursComparisonGrid>
             <HoursBox>
               <SmallText style={{ marginBottom: '4px', color: theme.colors.primary.light }}>
                 Yesterday's Hours
               </SmallText>
               <div style={{ fontSize: '18px', fontWeight: '600', color: theme.colors.primary.main }}>
                 {formatHoursAndMinutes(parseFloat(hoursWorked))}
               </div>
             </HoursBox>
             
             <ArrowIcon>
               <FiArrowRight />
             </ArrowIcon>
             
             <HoursBox highlight>
               <SmallText style={{ marginBottom: '4px', color: theme.colors.secondary.green }}>
                 Optimal Hours Today
               </SmallText>
               <div style={{ fontSize: '18px', fontWeight: '600', color: theme.colors.secondary.green }}>
                 {formatHoursAndMinutes(optimalPace.hours.optimal)}
               </div>
             </HoursBox>
           </HoursComparisonGrid>
           
           {optimalPace.demandPoints && (
             <HoursComparisonGrid>
               <HoursBox>
                 <SmallText style={{ marginBottom: '4px', color: theme.colors.primary.light }}>
                   Yesterday's Demand Points
                 </SmallText>
                 <div style={{ fontSize: '18px', fontWeight: '600', color: theme.colors.primary.main }}>
                   {demandPoints}
                 </div>
               </HoursBox>
               
               <ArrowIcon>
                 <FiArrowRight />
               </ArrowIcon>
               
               <HoursBox highlight>
                 <SmallText style={{ marginBottom: '4px', color: theme.colors.secondary.green }}>
                   Optimal Demand Points Today
                 </SmallText>
                 <div style={{ fontSize: '18px', fontWeight: '600', color: theme.colors.secondary.green }}>
                   {optimalPace.demandPoints.optimal}
                 </div>
               </HoursBox>
             </HoursComparisonGrid>
           )}
           
           <SmallText>
             <strong>Hours Adjustment:</strong> {optimalPace.hours.optimal > parseFloat(hoursWorked) ? 'Increase' : 'Decrease'} by{' '}
             {Math.round(Math.abs((optimalPace.hours.optimal / parseFloat(hoursWorked) - 1) * 100))}% 
             ({formatHoursAndMinutes(Math.abs(optimalPace.hours.optimal - parseFloat(hoursWorked)))}) to reach optimal arousal level
             {optimalPace.demandPoints && (
               <>
                 <br/>
                 <strong>Demand Points Adjustment:</strong> {optimalPace.demandPoints.optimal > parseFloat(demandPoints) ? 'Increase' : 'Decrease'} by{' '}
                 {Math.round(Math.abs((optimalPace.demandPoints.optimal / parseFloat(demandPoints) - 1) * 100))}% 
                 ({Math.abs(optimalPace.demandPoints.optimal - parseFloat(demandPoints))} points)
               </>
             )}
           </SmallText>
        </VisualizationCard>
      )}

      <Grid columns="1fr" desktopColumns="2fr 1fr">
        {/* Input Form */}
        <FormSection>
          <Heading2>
            <FiEdit3 style={{ marginRight: '8px' }} />
            Work Data & Emotional Factors
          </Heading2>
          
          <InputGroup>
            <Label htmlFor="hours">Hours Worked Yesterday</Label>
            <Input
              id="hours"
              type="number"
              min="0"
              max="24"
              step="0.5"
              value={hoursWorked}
              onChange={(e) => setHoursWorked(e.target.value)}
              placeholder="8.5"
            />
          </InputGroup>

          <InputGroup>
            <Label htmlFor="demandPoints">Demand Points Yesterday (Optional)</Label>
            <Input
              id="demandPoints"
              type="number"
              min="0"
              step="1"
              value={demandPoints}
              onChange={(e) => setDemandPoints(e.target.value)}
              placeholder="15"
            />
            <SmallText style={{ marginTop: theme.spacing.xs, marginBottom: '0' }}>
              Effort-based measurement (whole numbers only)
            </SmallText>
          </InputGroup>

          <Heading3>Emotional Factors (0-10 scale)</Heading3>
          <SmallText style={{ marginBottom: theme.spacing.lg }}>
            Rate each factor where 10 = best/highest. At least pressure is required for calculation. 
            Note: Higher emotional factor scores reduce arousal in the Yerkes-Dodson calculation.
          </SmallText>
          
          <EmotionalFactorsGrid>
            <FactorInput>
              <Label htmlFor="pressure">Pressure *</Label>
              <Input
                id="pressure"
                type="number"
                min="0"
                max="10"
                step="0.1"
                value={emotionalFactors.pressure}
                onChange={(e) => handleFactorChange('pressure', e.target.value)}
                placeholder="0-10"
              />
            </FactorInput>
            
            <FactorInput>
              <Label htmlFor="hours">Personal Identity</Label>
              <Input
                id="hours"
                type="number"
                min="0"
                max="10"
                step="0.1"
                value={emotionalFactors.hours}
                onChange={(e) => handleFactorChange('hours', e.target.value)}
                placeholder="0-10"
              />
            </FactorInput>
            
            <FactorInput>
              <Label htmlFor="focus">Focus</Label>
              <Input
                id="focus"
                type="number"
                min="0"
                max="10"
                step="0.1"
                value={emotionalFactors.focus}
                onChange={(e) => handleFactorChange('focus', e.target.value)}
                placeholder="0-10"
              />
            </FactorInput>
            
            <FactorInput>
              <Label htmlFor="calm">Calm</Label>
              <Input
                id="calm"
                type="number"
                min="0"
                max="10"
                step="0.1"
                value={emotionalFactors.calm}
                onChange={(e) => handleFactorChange('calm', e.target.value)}
                placeholder="0-10"
              />
            </FactorInput>
            
            <FactorInput>
              <Label htmlFor="happiness">Positivity</Label>
              <Input
                id="happiness"
                type="number"
                min="0"
                max="10"
                step="0.1"
                value={emotionalFactors.happiness}
                onChange={(e) => handleFactorChange('happiness', e.target.value)}
                placeholder="0-10"
              />
            </FactorInput>
            
            <FactorInput>
              <Label htmlFor="depression">Satisfaction</Label>
              <Input
                id="depression"
                type="number"
                min="0"
                max="10"
                step="0.1"
                value={emotionalFactors.depression}
                onChange={(e) => handleFactorChange('depression', e.target.value)}
                placeholder="0-10"
              />
            </FactorInput>
            
            <FactorInput>
              <Label htmlFor="mania">Control</Label>
              <Input
                id="mania"
                type="number"
                min="0"
                max="10"
                step="0.1"
                value={emotionalFactors.mania}
                onChange={(e) => handleFactorChange('mania', e.target.value)}
                placeholder="0-10"
              />
            </FactorInput>
            
            <FactorInput>
              <Label htmlFor="energy">Energy</Label>
              <Input
                id="energy"
                type="number"
                min="0"
                max="10"
                step="0.1"
                value={emotionalFactors.energy}
                onChange={(e) => handleFactorChange('energy', e.target.value)}
                placeholder="0-10"
              />
            </FactorInput>
          </EmotionalFactorsGrid>
        </FormSection>

        {/* Results Display */}
        <div>
                     {optimalPace ? (
             <Card>
               <OptimalPaceDisplay>
                 <Heading2>
                   <FiClock style={{ marginRight: '8px' }} />
                   Optimal Pace for Today
                 </Heading2>
                 
                 <MainPace>{formatHoursAndMinutes(optimalPace.hours.optimal)}</MainPace>
                 
                 <StatusBadge status={getOptimalPaceStatus()}>
                   Recommended Work Hours
                 </StatusBadge>
                 
                 <PaceRange>
                   Range: {formatHoursAndMinutes(optimalPace.hours.min)} - {formatHoursAndMinutes(optimalPace.hours.max)}
                 </PaceRange>
                 
                 {optimalPace.demandPoints && (
                   <>
                     <div style={{ marginTop: theme.spacing.lg, borderTop: `1px solid ${theme.colors.primary.light}`, paddingTop: theme.spacing.lg }}>
                       <Heading3 style={{ marginBottom: theme.spacing.md, textAlign: 'center' }}>
                         Demand Points
                       </Heading3>
                       <div style={{ fontSize: '48px', fontWeight: '700', fontFamily: theme.typography.heading.fontFamily, color: theme.colors.secondary.green, lineHeight: '1.2', margin: `${theme.spacing.md} 0`, textAlign: 'center' }}>
                         {optimalPace.demandPoints.optimal}
                       </div>
                       <div style={{ fontSize: '18px', color: theme.colors.primary.light, marginBottom: theme.spacing.md, textAlign: 'center' }}>
                         Range: {optimalPace.demandPoints.min} - {optimalPace.demandPoints.max}
                       </div>
                     </div>
                   </>
                 )}
                 
                 <SmallText>
                   Current arousal level: {optimalPace.currentArousal}/10<br/>
                   Optimal arousal: {optimalPace.optimalArousal}/10<br/>
                   Task complexity: {optimalPace.isHighComplexity ? 'High' : 'Low'} (pressure: {optimalPace.pressure}/10)
                 </SmallText>
               </OptimalPaceDisplay>
             </Card>
           ) : (
            <Card style={{ textAlign: 'center' }}>
              <FiTrendingUp style={{ fontSize: '48px', color: theme.colors.primary.light, marginBottom: theme.spacing.md }} />
              <Heading2>Enter Your Data</Heading2>
              <BodyText>
                Provide hours worked yesterday and pressure level to calculate your optimal pace
              </BodyText>
            </Card>
          )}

          <InfoCard>
            <Heading3>
              <FiInfo style={{ marginRight: '8px' }} />
              About Yerkes-Dodson Law
            </Heading3>
            <BodyText>
              The Yerkes-Dodson Law shows that performance increases with arousal up to a point, 
              then decreases. For complex tasks (pressure ≥ 6), the optimal arousal zone is narrower 
              (1-3, optimal: 2) compared to simple tasks (1-5, optimal: 3).
            </BodyText>
            <SmallText>
              This calculation uses proportional scaling based on the ratio between optimal and 
              current arousal levels. Both work hours and demand points are adjusted by the same 
              percentage (capped at 100% increase) to achieve optimal arousal. Hours are also 
              capped at 18 hours maximum for health and safety.
            </SmallText>
            
            <HBRReference>
              <SmallText style={{ marginBottom: '4px', fontWeight: '600' }}>
                📖 Research Reference
              </SmallText>
              <SmallText style={{ marginBottom: '0' }}>
                Learn more about stress and productivity: {' '}
                <a 
                  href="https://hbr.org/2016/04/are-you-too-stressed-to-be-productive-or-not-stressed-enough" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  Harvard Business Review - Are You Too Stressed to Be Productive?
                </a>
              </SmallText>
            </HBRReference>
          </InfoCard>
        </div>
      </Grid>
    </Container>
  )
}

export default OptimalPacePage 